import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { PageHero } from "@/components/site/PageHero";
import { AdvisorCTA } from "@/components/site/AdvisorCTA";
import { caseStudyBySlug, type CaseStudy, type Inline } from "@/content/case-studies";
import { SITE_URL } from "@/lib/seo";

export const Route = createFileRoute("/case-studies/$slug")({
  loader: ({ params }): { study: CaseStudy } => {
    const study = caseStudyBySlug(params.slug);
    if (!study) throw notFound();
    return { study };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) {
      return { meta: [{ title: "Not found" }, { name: "robots", content: "noindex" }] };
    }
    const { study } = loaderData;
    const url = `${SITE_URL}/case-studies/${params.slug}/`;
    return {
      links: [{ rel: "canonical", href: url }],
      meta: [
        { title: study.metaTitle },
        { name: "description", content: study.metaDescription },
        { property: "og:title", content: study.metaTitle },
        { property: "og:description", content: study.metaDescription },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
      ],
    };
  },
  notFoundComponent: CaseStudyMissing,
  component: CaseStudyPage,
});

function CaseStudyMissing() {
  return (
    <div className="mx-auto max-w-3xl px-6 py-24 text-center">
      <p className="text-lg text-muted-foreground">We couldn't find that case study.</p>
      <Link to="/case-studies/" className="mt-6 inline-block font-semibold text-primary">
        See all case studies
      </Link>
    </div>
  );
}

function renderInline(parts: Inline[]) {
  return parts.map((p, i) =>
    typeof p === "string" ? (
      <span key={i}>{p}</span>
    ) : (
      <Link
        key={i}
        to={p.to}
        className="font-semibold text-primary underline-offset-4 hover:underline"
      >
        {p.text}
      </Link>
    ),
  );
}

function CaseStudyPage() {
  const { study } = Route.useLoaderData();
  const poppins = { fontFamily: "'Poppins', sans-serif" } as const;

  return (
    <>
      <PageHero
        maxWidth="6xl"
        headline={study.headline}
        {...(study.intro ? { supporting: study.intro } : {})}
      />

      <section className="bg-background">
        <div className="mx-auto max-w-3xl px-6 py-14 md:py-20">
          <img
            src={study.image}
            alt={study.imageAlt}
            className="w-full rounded-2xl border border-border/60 object-cover"
          />
          <dl className="mt-8 grid grid-cols-2 gap-4 rounded-2xl bg-lavender p-6 sm:grid-cols-4">
            {study.facts.map((f) => (
              <div key={f.label}>
                <dt className="text-[11px] font-semibold uppercase tracking-wider text-primary">
                  {f.label}
                </dt>
                <dd className="mt-1 text-sm font-semibold text-ink">{f.value}</dd>
              </div>
            ))}
          </dl>

          <div className="mt-10 space-y-5 text-base leading-relaxed text-muted-foreground">
            {study.blocks.map((b, i) => {
              switch (b.t) {
                case "h2":
                  return (
                    <h2
                      key={i}
                      className="pt-6 text-2xl font-semibold text-ink md:text-3xl"
                      style={poppins}
                    >
                      {b.x}
                    </h2>
                  );
                case "h3":
                  return (
                    <h3 key={i} className="pt-2 text-lg font-semibold text-ink" style={poppins}>
                      {b.x}
                    </h3>
                  );
                case "p":
                  return <p key={i}>{renderInline(b.x)}</p>;
                case "quote":
                  return (
                    <blockquote
                      key={i}
                      className="rounded-2xl border-l-4 border-[#693D94] bg-lavender p-6 text-lg italic leading-relaxed text-ink"
                    >
                      “{b.x}”
                    </blockquote>
                  );
                case "ul":
                  return (
                    <ul key={i} className="space-y-3">
                      {b.items.map((it) => (
                        <li key={it} className="flex gap-3">
                          <span
                            className="mt-2 inline-block h-2 w-2 flex-shrink-0 rounded-full bg-[#693D94]"
                            aria-hidden="true"
                          />
                          <span>{it}</span>
                        </li>
                      ))}
                    </ul>
                  );
              }
            })}
          </div>

          {!study.ctaTitle && (
            <Link
              to="/contact/"
              className="mt-8 inline-flex items-center rounded-[6px] bg-[#693D94] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#693D94]/90"
            >
              {study.ctaLabel}
            </Link>
          )}

          <Link
            to="/case-studies/"
            className="mt-10 block text-sm font-semibold text-primary hover:underline"
          >
            ← All case studies
          </Link>
        </div>
      </section>

      {study.ctaTitle && (
        <AdvisorCTA
          title={study.ctaTitle}
          primaryLabel={study.ctaLabel}
          primaryTo="/contact/"
          secondaryLabel="All case studies"
          secondaryTo="/case-studies/"
        />
      )}
    </>
  );
}
